import { useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AutoTextarea } from "@/components/AutoTextarea";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";

export const GRAMMAR = [
  ["sounds", "Sounds & pronunciation"], ["word_order", "Sentence structure"], ["verbs", "Verbs"], ["nouns", "Nouns & pronouns"],
  ["writing", "Written form"], ["spoken_vs_written", "Spoken vs written"], ["other", "Other notes"],
];

export function GrammarEditor({ lang, canEdit }) {
  const invalidate = useInvalidate();
  const [description, setDescription] = useState(lang.description || "");
  const [grammar, setGrammar] = useState(lang.grammar || {});
  const [busy, setBusy] = useState("");
  const dirty = description !== (lang.description || "") || GRAMMAR.some(([k]) => (grammar[k] || "") !== (lang.grammar?.[k] || ""));

  const organize = async () => {
    setBusy("ai");
    try {
      const { data } = await api.post(`/languages/${lang.id}/ai/organize`, { text: description });
      setGrammar(Object.fromEntries(GRAMMAR.map(([k]) => [k, [grammar[k], data.grammar[k]].filter(Boolean).join("\n\n")])));
      toast.success("Sorted into sections. Review and save.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const save = async () => {
    setBusy("save");
    try {
      await api.patch(`/languages/${lang.id}`, { description, grammar });
      invalidate(["language", lang.id], ["languages", lang.universe_id]);
      toast.success("Language saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-8" data-testid="grammar-editor">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="lf-label">Overview</div>
          {canEdit && <Button size="sm" variant="outline" onClick={organize} disabled={!!busy || description.trim().length < 3} data-testid="grammar-organize-button">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />{busy === "ai" ? "Sorting…" : "Sort into sections"}</Button>}
        </div>
        <AutoTextarea value={description} onChange={(e) => setDescription(e.target.value)} disabled={!canEdit} maxVh={50}
          className="min-h-[140px] leading-relaxed" placeholder="Who speaks it, where it came from, how it feels…" data-testid="grammar-description" />
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {GRAMMAR.map(([k, label]) => (
          <div key={k}>
            <div className="lf-label mb-2 text-[#14B8A6]">{label}</div>
            <AutoTextarea value={grammar[k] || ""} onChange={(e) => setGrammar({ ...grammar, [k]: e.target.value })} disabled={!canEdit} maxVh={40}
              className="min-h-[110px] leading-relaxed text-sm" placeholder={canEdit ? "Not described yet" : "—"} data-testid={`grammar-${k}`} />
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="sticky bottom-4 flex justify-end">
          <Button onClick={save} disabled={!dirty || !!busy} data-testid="grammar-save-button">{busy === "save" ? "Saving…" : "Save language"}</Button>
        </div>
      )}
    </div>
  );
}
